/**
 * ui.tsx — the small presentational primitives shared by every screen, ported from the
 * mockup's markup so the CSS class names of `styles/theme.css` apply unchanged.
 *
 * Rules enforced here (spec §8, mockup-rules.md):
 *  - money direction is never colour alone: `<Money>` renders the sign and, with `arrow`,
 *    an ↗/↘ icon;
 *  - every number, date, mask and currency is LTR-isolated (`.num` / `.ltr`);
 *  - status is chip + icon + text.
 *
 * REDESIGN §7: `ProviderLogo`, `CategoryIcon` and `EmptyState` keep their call signatures but
 * now DRAW — they delegate to `shared/ui` (the provider marks, the category glyphs and the
 * empty-state illustrations), so every screen that already used them gained the pictures
 * without a change of its own. No emoji, no external request; every string still arrives from
 * `i18n/he.ts`.
 */

import type { ReactNode } from 'react';
import { Icon } from './Icon.tsx';
import { fmtMoney } from '../format.ts';
import { providerName } from '../i18n/he.ts';
import type { GroupKey, ProviderId } from '../../../shared-types.ts';
import {
  CategoryIcon as KitCategoryIcon,
  EmptyState as KitEmptyState,
  ProviderMark,
} from '../../../../shared/ui/src/index.tsx';
import type { IllustrationName } from '../../../../shared/ui/src/index.tsx';

/** An LTR-isolated span for numbers, dates, masks and currency. */
export function Num({ children, tabular, className }: { children: ReactNode; tabular?: boolean; className?: string }) {
  return <span className={`num${tabular ? ' tab' : ''}${className ? ` ${className}` : ''}`}>{children}</span>;
}

export function Ltr({ children }: { children: ReactNode }) {
  return <span className="ltr">{children}</span>;
}

/** Money with the spec's formatting; `arrow` adds the direction icon (never colour alone). */
export function Money({
  agorot,
  decimals = 0,
  plus,
  arrow,
  className,
  tabular,
}: {
  agorot: number | null | undefined;
  decimals?: number;
  plus?: boolean;
  arrow?: boolean;
  className?: string;
  tabular?: boolean;
}) {
  const negative = (agorot ?? 0) < 0;
  return (
    <span className={`num${tabular ? ' tab' : ''}${className ? ` ${className}` : ''}`}>
      {arrow ? <Icon name={negative ? 'downLeft' : 'upRight'} className="xs" /> : null}
      {fmtMoney(agorot, decimals, plus)}
    </span>
  );
}

export function Chip({
  children,
  tone,
  icon,
  title,
}: {
  children: ReactNode;
  tone?: 'good' | 'warn' | 'critical' | 'info' | 'accent' | 'muted';
  icon?: string;
  title?: string;
}) {
  return (
    <span className={`chip${tone ? ` ${tone}` : ''}`} title={title}>
      {icon ? <Icon name={icon} /> : null}
      {children}
    </span>
  );
}

export function Tag({ children, tone, icon }: { children: ReactNode; tone?: 'critical' | 'warn'; icon?: string }) {
  return (
    <span className={`tag${tone ? ` ${tone}` : ''}`}>
      {icon ? <Icon name={icon} className="xs" /> : null}
      {children}
    </span>
  );
}

/**
 * The provider's mark (REDESIGN §7a) — our own drawn monogram in a colour that evokes the
 * institution, never the bank's logo file. `title` makes it an `img` with an accessible name;
 * omit it wherever the row already names the provider in text.
 */
export function ProviderLogo({
  providerId,
  size,
  title,
}: {
  providerId: ProviderId;
  size?: 'xs' | 'lg';
  title?: string;
}) {
  const px = size === 'xs' ? 20 : size === 'lg' ? 40 : 28;
  return <ProviderMark providerId={providerId} size={px} title={title} />;
}

/** A provider mark that always names itself — for rows where the mark is the only identifier. */
export function AccountMark({ providerId, size }: { providerId: ProviderId; size?: 'xs' | 'lg' }) {
  return <ProviderLogo providerId={providerId} size={size} title={providerName(providerId)} />;
}

/** The category's drawn glyph on its soft hue (REDESIGN §7b). Never a blank. */
export function CategoryIcon({ groupKey, small, title }: { groupKey: GroupKey; small?: boolean; title?: string }) {
  return <KitCategoryIcon groupKey={groupKey} soft size={small ? 16 : 20} title={title} />;
}

/** The mockup's old `icon` prop, mapped onto the six illustrations of REDESIGN §7d. */
const ILLUSTRATION_OF: Readonly<Record<string, IllustrationName>> = {
  bell: 'noAlerts',
  alert: 'reconnect',
  link: 'reconnect',
  inbox: 'noResults',
  search: 'noResults',
  card: 'noResults',
  bars: 'noAnomalies',
  trend: 'noAnomalies',
  checkCircle: 'noAnomalies',
  db: 'firstRun',
};

/**
 * An empty (or healthy) state: line illustration + heading + one sentence + optional action
 * (REDESIGN §7d) — never a bare "אין נתונים". Existing call sites keep passing `icon`;
 * new ones name the illustration directly.
 */
export function EmptyState({
  illustration,
  icon = 'inbox',
  title,
  body,
  action,
  variant,
}: {
  illustration?: IllustrationName;
  icon?: string;
  title: string;
  body?: string;
  action?: ReactNode;
  variant?: 'page' | 'inline';
}) {
  return (
    <KitEmptyState
      illustration={illustration ?? ILLUSTRATION_OF[icon] ?? 'noResults'}
      title={title}
      body={body}
      action={action}
      variant={variant}
    />
  );
}

export function Notice({ tone, icon, children }: { tone?: 'warn' | 'info' | 'good' | 'critical'; icon?: string; children: ReactNode }) {
  return (
    <div className={`notice${tone ? ` ${tone}` : ''}`}>
      <Icon name={icon ?? (tone === 'warn' || tone === 'critical' ? 'alert' : 'info')} />
      <span>{children}</span>
    </div>
  );
}

/** A section: heading on the right (RTL), action link on the left (spec §4 conventions). */
export function Section({
  title,
  titleId,
  sub,
  action,
  children,
  span,
  variant,
  className,
  ariaLabel,
}: {
  title?: ReactNode;
  titleId?: string;
  sub?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  span?: number;
  variant?: 'hero' | 'panel';
  className?: string;
  ariaLabel?: string;
}) {
  const style = span ? ({ ['--c' as string]: String(span) } as Record<string, string>) : undefined;
  return (
    <section
      className={`sec${variant ? ` ${variant}` : ''}${className ? ` ${className}` : ''}`}
      style={style}
      aria-labelledby={titleId}
      aria-label={ariaLabel}
    >
      {title ? (
        <div className="sec-h">
          <h2 id={titleId}>
            {title}
            {sub ? <span className="sub"> {sub}</span> : null}
          </h2>
          {action ? <span className="act">{action}</span> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** Utilisation meter (spec §6.7) — always accompanied by text, never colour alone. */
export function Meter({ ratio, label }: { ratio: number; label: string }) {
  const pct = Math.min(100, Math.round(ratio * 100));
  const tone = ratio > 0.9 ? 'critical' : ratio > 0.7 ? 'warn' : '';
  return (
    <div className="meter" role="meter" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <div className={`fill${tone ? ` ${tone}` : ''}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Month progress bar with the optional "next charge" marker (spec §6.8). */
export function ProgressBar({
  value,
  max,
  label,
  markRatio,
  markLabel,
}: {
  value: number;
  max: number;
  label: string;
  markRatio?: number | null;
  markLabel?: string;
}) {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="progress" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max} aria-label={label}>
      <div className="fill" style={{ width: `${pct}%` }} />
      {markRatio != null ? (
        <div className="mark" style={{ insetInlineStart: `${Math.min(100, Math.round(markRatio * 100))}%` }} data-l={markLabel} title={markLabel} />
      ) : null}
    </div>
  );
}

export function Skeleton({ height, span }: { height: number; span?: number }) {
  return <div className="sk" style={{ height: `${height}px`, gridColumn: `span ${span ?? 12}` }} />;
}

/** The first-load skeleton of a 12-column screen (spec §3.5 — first load only). */
export function ScreenSkeleton() {
  return (
    <div className="screen" aria-busy="true" aria-label="טוען">
      <div className="g12">
        <Skeleton height={180} span={8} />
        <Skeleton height={180} span={4} />
        <Skeleton height={300} span={7} />
        <Skeleton height={300} span={5} />
        <Skeleton height={220} span={7} />
        <Skeleton height={220} span={5} />
      </div>
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="switch"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    />
  );
}

export function ToggleGroup<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: ReadonlyArray<readonly [T, string]>;
  onChange: (next: T) => void;
  label: string;
}) {
  return (
    <div className="toggle-group" role="group" aria-label={label}>
      {options.map(([key, text]) => (
        <button key={key} type="button" aria-pressed={value === key} onClick={() => onChange(key)}>
          {text}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: ReadonlyArray<readonly [T, string]>;
  onChange: (next: T) => void;
  label: string;
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map(([key, text]) => (
        <button key={key} type="button" aria-pressed={value === key} onClick={() => onChange(key)}>
          {text}
        </button>
      ))}
    </div>
  );
}

/** A `<div class="stat">` tile: label above, value below, optional delta line. */
export function Stat({
  label,
  value,
  delta,
  size,
  tone,
  className,
}: {
  label?: ReactNode;
  value: ReactNode;
  delta?: ReactNode;
  size?: 'big' | 'huge';
  tone?: 'good' | 'critical' | 'warn';
  className?: string;
}) {
  return (
    <div className={`stat${className ? ` ${className}` : ''}`}>
      {label ? <span className="l">{label}</span> : null}
      <span className={`v${size ? ` ${size}` : ''}${tone ? ` ${tone}` : ''}`}>{value}</span>
      {delta ? <span className="d">{delta}</span> : null}
    </div>
  );
}
